import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { BRAND_GEOMETRY } from './brand-geometry';

export type BrandVariant = 'principale' | 'compatta' | 'pittogramma';

const MAIN = BRAND_GEOMETRY.main;
const COMPACT = BRAND_GEOMETRY.compact;
const PICTO = BRAND_GEOMETRY.pictogram;

/** Rapporto larghezza/lato del pittogramma, dalla geometria. */
const ratio = (viewBox: string, side: number): number => Number(viewBox.split(' ')[2]) / side;
const MAIN_RATIO = ratio(MAIN.viewBox, Number(MAIN.viewBox.split(' ')[3]));
const COMPACT_RATIO = ratio(COMPACT.viewBox, Number(COMPACT.viewBox.split(' ')[3]));

/** Larghezze minime del manuale (pagina 06): sotto, si passa alla versione più semplice. */
const MIN_MAIN_WIDTH = 100;
const MIN_COMPACT_WIDTH = 72;

/**
 * Marchio B.Site (FR-054). `size` è il lato del pittogramma in pixel. La versione si semplifica
 * da sola sotto le larghezze minime e intorno resta l'area di rispetto, un quarto del lato.
 * In tema scuro il marchio passa al negativo: nome bianco, triangoli e payoff gialli.
 */
@Component({
  selector: 'bs-brand-mark',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'inline-flex shrink-0',
    '[style.padding.px]': 'clearSpace()',
    '[attr.data-variant]': 'effective()',
  },
  template: `
    <svg
      [attr.viewBox]="viewBox()"
      [attr.width]="width()"
      [attr.height]="size()"
      role="img"
      [attr.aria-label]="effective() === 'principale' ? 'B.Site, il cantiere condiviso' : 'B.Site'"
      focusable="false"
    >
      <g [attr.transform]="pictogramTransform()">
        <path [attr.d]="picto.square" class="fill-bsite-400" />
        <path [attr.d]="picto.letter" class="fill-ink" />
        <path [attr.d]="picto.triangle" [class]="accentClass" />
      </g>
      @if (effective() === 'compatta') {
        <path [attr.d]="compact.letters" [class]="nameClass" />
        <path [attr.d]="compact.triangle" [class]="accentClass" />
      } @else if (effective() === 'principale') {
        <path [attr.d]="main.letters" [class]="nameClass" />
        <path [attr.d]="main.triangle" [class]="accentClass" />
        <path [attr.d]="main.payoff" [class]="accentClass" />
      }
    </svg>
  `,
})
export class BrandMarkComponent {
  readonly variant = input<BrandVariant>('compatta');
  /** Lato del pittogramma in pixel. */
  readonly size = input(24);

  protected readonly picto = PICTO;
  protected readonly compact = COMPACT;
  protected readonly main = MAIN;
  protected readonly nameClass = 'fill-ink dark:fill-white';
  protected readonly accentClass = 'fill-ink dark:fill-bsite-400';

  protected readonly effective = computed<BrandVariant>(() => {
    const size = this.size();
    let variant = this.variant();
    if (variant === 'principale' && size * MAIN_RATIO < MIN_MAIN_WIDTH) {
      variant = 'compatta';
    }
    if (variant === 'compatta' && size * COMPACT_RATIO < MIN_COMPACT_WIDTH) {
      variant = 'pittogramma';
    }
    return variant;
  });

  protected readonly viewBox = computed(() => {
    switch (this.effective()) {
      case 'principale':
        return MAIN.viewBox;
      case 'compatta':
        return COMPACT.viewBox;
      default:
        return PICTO.viewBox;
    }
  });

  protected readonly width = computed(() => {
    const size = this.size();
    switch (this.effective()) {
      case 'principale':
        return size * MAIN_RATIO;
      case 'compatta':
        return size * COMPACT_RATIO;
      default:
        return size;
    }
  });

  protected readonly pictogramTransform = computed(() => {
    switch (this.effective()) {
      case 'principale':
        return `scale(${MAIN.pictogramScale})`;
      case 'compatta':
        return `scale(${COMPACT.pictogramScale})`;
      default:
        return null;
    }
  });

  /** Area di rispetto: un quarto del lato del pittogramma (manuale, pagina 06). */
  protected readonly clearSpace = computed(() => this.size() / 4);
}
